import { useCallback, useEffect, useState } from "react";
import { fetchPendingRefunds } from "./paymentsApi";

/**
 * Normalizes the response into an array.
 * Accepts raw arrays or { items: [...] } / { payments: [...] } envelopes.
 * @param {*} data - Decrypted API payload
 * @returns {Array}
 */
function normalizeRefunds(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.payments)) return data.payments;
    return [];
}

/**
 * Inner async loader; kept outside the hook so it stays under 25 lines.
 * @param {Object} args - Loader arguments
 */
async function loadPending({ signal, setRefunds, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const data = await fetchPendingRefunds();
        if (!signal.aborted) setRefunds(normalizeRefunds(data));
    } catch (err) {
        if (!signal.aborted) setError(err?.response?.data || "Impossible de charger les remboursements");
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Custom hook listing payments stuck in REFUND_MANUAL_REQUIRED status.
 * @returns {{ refunds: Array, loading: boolean, error: any, refresh: Function }}
 */
function useRefundsPending() {
    const [refunds, setRefunds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        loadPending({ signal: controller.signal, setRefunds, setError, setLoading });
        return () => controller.abort();
    }, [tick]);

    const refresh = useCallback(() => setTick((t) => t + 1), []);

    return { refunds, loading, error, refresh };
}

export default useRefundsPending;
